// What the phone board shows while the schema and the saved layout load. It has
// the loaded board's shape (the profile row, the tab strip, the room of a
// column's control row, then cards), so nothing moves when the board arrives:
// a one-column skeleton under a short row used to jump by the height of the tab
// strip and the taller profile row.

import { Box } from '@mui/material';
import { CardSkeleton } from '../card/CardSkeleton';
import { TAB_HEIGHT } from './ColumnTabs';

/** A 44 px profile control with 4 px above and below. */
export const PHONE_PROFILE_ROW_HEIGHT = 52;
/** A column's control row: a 44 px control with 8 px above and below. */
export const PHONE_CONTROL_ROW_HEIGHT = 60;

export interface PhoneBoardSkeletonProps {
    /** How many tabs the loaded strip will have. */
    tabCount: number;
}

export function PhoneBoardSkeleton({ tabCount }: PhoneBoardSkeletonProps) {
    return (
        <>
            <Box
                data-testid="board-skeleton-profiles"
                sx={{ height: PHONE_PROFILE_ROW_HEIGHT, flexShrink: 0 }}
            />
            <Box
                data-testid="board-skeleton-tabs"
                aria-hidden="true"
                sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${tabCount}, minmax(0, 1fr))`,
                    flexShrink: 0,
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                {Array.from({ length: tabCount }, (_, i) => (
                    <Box
                        key={i}
                        sx={{
                            height: TAB_HEIGHT,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                width: 56,
                                height: 12,
                                borderRadius: '6px',
                                bgcolor: 'action.hover',
                            }}
                        />
                    </Box>
                ))}
            </Box>
            <Box
                sx={{ height: PHONE_CONTROL_ROW_HEIGHT, flexShrink: 0 }}
                aria-hidden="true"
            />
            <Box
                data-testid="board-skeleton-cards"
                sx={{
                    flex: 1,
                    minHeight: 0,
                    px: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    overflow: 'hidden',
                }}
            >
                <CardSkeleton />
                <CardSkeleton />
            </Box>
        </>
    );
}
